import React, { useEffect, useState } from 'react';
import { RefreshCw, MessageSquare } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { usePermission, useRole } from '../../../hooks/useAuth';
import { LoadingSpinner } from '../../ui/Loading';
import Button from '../../ui/Button';
import AccessDeniedPanel from '../../ui/AccessDeniedPanel';
import { PageHeader } from '@kioskgaming/ui';
import { Card, CardContent } from '../../ui/Card';
import { Input } from '@/components/ui/shadcn';
import { apiClient } from '../../../services/api';
import type { MessagingPlatformSettings } from '../../../types';

const MessagingSettingsManagement: React.FC = () => {
  const canManage = usePermission('transactionLimitsManagement');
  const canEdit = useRole(['system_admin', 'super_admin', 'finance_admin']);
  const [settings, setSettings] = useState<MessagingPlatformSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiClient.getMessagingSettings();
      if (res.success && res.data) setSettings(res.data);
    } catch {
      toast.error('Failed to load messaging settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (canManage) void load();
  }, [canManage]);

  const save = async () => {
    if (!settings || !canEdit) return;
    setSaving(true);
    try {
      const res = await apiClient.updateMessagingSettings(settings);
      if (res.success && res.data) {
        setSettings(res.data);
        toast.success('Messaging settings saved');
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      toast.error(msg || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!canManage) {
    return (
      <AccessDeniedPanel message="You don't have permission to manage messaging settings." />
    );
  }

  if (loading || !settings) {
    return (
      <div className="flex items-center justify-center h-96">
        <LoadingSpinner />
      </div>
    );
  }

  const setNum = (key: keyof MessagingPlatformSettings, value: string) => {
    const n = parseFloat(value);
    setSettings({ ...settings, [key]: Number.isFinite(n) ? n : 0 });
  };

  const setInt = (key: keyof MessagingPlatformSettings, value: string) => {
    const n = parseInt(value, 10);
    setSettings({ ...settings, [key]: Number.isFinite(n) ? n : 0 });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Messaging pricing & limits"
        description="Platform-wide email/SMS unit prices, channel toggles, and guardrails for shop player messaging."
        actions={
          <Button onClick={() => void load()} variant="outline" size="sm">
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        }
      />

      <Card>
        <CardContent className="pt-6 space-y-6">
          <div className="flex items-center gap-2 text-muted-foreground">
            <MessageSquare className="h-5 w-5" />
            <span className="text-sm">Shops are billed from USD wallet per successful delivery.</span>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <label className="space-y-1 text-sm">
              <span className="font-medium">Email unit price (USD)</span>
              <Input
                type="number"
                step="0.0001"
                min="0"
                disabled={!canEdit}
                value={settings.emailUnitPriceUsd}
                onChange={(e) => setNum('emailUnitPriceUsd', e.target.value)}
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium">SMS unit price per segment (USD)</span>
              <Input
                type="number"
                step="0.0001"
                min="0"
                disabled={!canEdit}
                value={settings.smsUnitPriceUsd}
                onChange={(e) => setNum('smsUnitPriceUsd', e.target.value)}
              />
            </label>
          </div>

          <div className="flex flex-wrap gap-6 text-sm">
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                disabled={!canEdit}
                checked={settings.emailEnabled}
                onChange={(e) => setSettings({ ...settings, emailEnabled: e.target.checked })}
              />
              Email channel enabled
            </label>
            <label className="inline-flex items-center gap-2">
              <input
                type="checkbox"
                disabled={!canEdit}
                checked={settings.smsEnabled}
                onChange={(e) => setSettings({ ...settings, smsEnabled: e.target.checked })}
              />
              SMS channel enabled (enable only after the SMS provider is configured)
            </label>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <label className="space-y-1 text-sm">
              <span className="font-medium">Max recipients / campaign</span>
              <Input
                type="number"
                min="1"
                disabled={!canEdit}
                value={settings.maxRecipientsPerCampaign}
                onChange={(e) => setInt('maxRecipientsPerCampaign', e.target.value)}
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium">Max campaigns / shop / day</span>
              <Input
                type="number"
                min="1"
                disabled={!canEdit}
                value={settings.maxCampaignsPerShopPerDay}
                onChange={(e) => setInt('maxCampaignsPerShopPerDay', e.target.value)}
              />
            </label>
            <label className="space-y-1 text-sm">
              <span className="font-medium">Min interval (minutes)</span>
              <Input
                type="number"
                min="0"
                disabled={!canEdit}
                value={settings.minCampaignIntervalMinutes}
                onChange={(e) => setInt('minCampaignIntervalMinutes', e.target.value)}
              />
            </label>
          </div>

          <label className="block space-y-1 text-sm">
            <span className="font-medium">Banned words (comma-separated)</span>
            <Input
              disabled={!canEdit}
              value={(settings.bannedWords || []).join(', ')}
              onChange={(e) =>
                setSettings({
                  ...settings,
                  bannedWords: e.target.value
                    .split(',')
                    .map((w) => w.trim())
                    .filter(Boolean),
                })
              }
            />
          </label>

          {canEdit && (
            <Button onClick={() => void save()} disabled={saving}>
              {saving ? 'Saving…' : 'Save settings'}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MessagingSettingsManagement;
